import { createSlice } from "@reduxjs/toolkit";
import {
  downloadDailyOrders,
  getDailyOrdersBreakdowns,
  getDailyOrdersDaily,
  getDailyOrdersDetails,
  getDailyOrdersHours,
  getDailyOrdersInsights,
  getDailyOrdersInsightsBreakdowns,
  getDailyOrdersInsightsCustomers,
  getDailyOrdersInsightsHourly,
  getDailyOrdersSummary,
  getDailyOrdersWeekday,
} from "./dailyOrdersV3Action";

const section = (data) => ({
  loading: false,
  error: null,
  data,
  latestRequestId: null,
});

// Shapes of apps/api/v3/daily_orders.py (CANA-907).
const initialState = {
  summary: section(null),
  daily: section({ results: [], previous: null }),
  weekday: section({ results: [] }),
  hours: section({ buckets: [], results: [], peak: null }),
  breakdowns: section({ payment_types: [], sources: [] }),
  details: section({ rows: [], meta: null }),
  insights: section(null),
  insightsHourly: section(null),
  insightsBreakdowns: section(null),
  insightsCustomers: section(null),
  download: { loading: false, error: null },
};

// Filters change faster than the sections answer: only the latest request of
// a section may write to it.
const addSectionCases = (builder, thunk, key) =>
  builder
    .addCase(thunk.pending, (state, action) => {
      state[key].loading = true;
      state[key].error = null;
      state[key].latestRequestId = action.meta.requestId;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = action.payload;
    })
    .addCase(thunk.rejected, (state, action) => {
      if (action.meta.requestId !== state[key].latestRequestId) return;
      state[key].loading = false;
      state[key].data = initialState[key].data;
      state[key].error = action.payload;
    });

// The four requests of the open day's window; a new day starts them empty so
// the window never shows the previous day's answer.
const DAY_KEYS = [
  "insights",
  "insightsHourly",
  "insightsBreakdowns",
  "insightsCustomers",
];

const dailyOrdersV3 = createSlice({
  name: "dailyOrdersV3",
  initialState,
  reducers: {
    resetDayInsights: (state) => {
      DAY_KEYS.forEach((key) => {
        state[key] = section(null);
      });
    },
  },
  extraReducers: (builder) => {
    addSectionCases(builder, getDailyOrdersSummary, "summary");
    addSectionCases(builder, getDailyOrdersDaily, "daily");
    addSectionCases(builder, getDailyOrdersWeekday, "weekday");
    addSectionCases(builder, getDailyOrdersHours, "hours");
    addSectionCases(builder, getDailyOrdersBreakdowns, "breakdowns");
    addSectionCases(builder, getDailyOrdersDetails, "details");
    addSectionCases(builder, getDailyOrdersInsights, "insights");
    addSectionCases(builder, getDailyOrdersInsightsHourly, "insightsHourly");
    addSectionCases(
      builder,
      getDailyOrdersInsightsBreakdowns,
      "insightsBreakdowns"
    );
    addSectionCases(
      builder,
      getDailyOrdersInsightsCustomers,
      "insightsCustomers"
    );
    builder
      .addCase(downloadDailyOrders.pending, (state) => {
        state.download.loading = true;
        state.download.error = null;
      })
      .addCase(downloadDailyOrders.fulfilled, (state) => {
        state.download.loading = false;
      })
      .addCase(downloadDailyOrders.rejected, (state, action) => {
        state.download.loading = false;
        state.download.error = action.payload;
      });
  },
});

export const { resetDayInsights } = dailyOrdersV3.actions;
export default dailyOrdersV3.reducer;
